import React, { useState, useMemo } from "react";
import {
  Box,
  Grid,
  Stack,
  Button,
  useTheme,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import AirIcon from "@mui/icons-material/Air";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";

import { StatCard, LineChart } from "../../../../../shared/components/charts";
import { DashboardCard } from "../../../../../shared/components/DashboardCard";
import {
  FilterDrawer,
  FilterState,
  getAppliedFilterCount,
  DurationSelector,
  DateRange,
  DashboardCardSkeleton,
  CohortGuard,
} from "../../../../../shared/components";
import {
  useBiometricsStats,
  useBiometricsTrends,
  useBiometricsPatterns,
} from "../hooks";
import {
  buildFilterParams,
  transformTrendsData,
  calculateGranularity,
} from "../../shared/utils";
import { useClientScope } from "../../../../../shared/contexts";
import {
  usePersistedDateRange,
  usePersistedFilters,
  useMetricsOverallRange,
  useMetricsStats,
} from "../../../../../shared/hooks";

const BIOMETRICS_AND_VITALS_DETAILS = {
  biometricTrends: `Displays daily variations across core vital metrics, such as heart rate, heart rate variability (HRV), sleep, strain, and recovery. These metrics reflect changes in physiological load, autonomic balance, and overall readiness.

By tracking trends over time, this view helps identify emerging patterns, sustained shifts in stress or recovery status, and potential early indicators of fatigue or improved adaptation. It provides a longitudinal perspective on workforce health, supporting proactive wellbeing and performance management.`,
  averagePaceOfAging: `Represents the average rate of biological aging across the workforce, derived from longitudinal trends in key physiological markers including SpO2, respiration rate, resting heart rate, heart rate variability (HRV), and skin temperature.

These metrics reflect cardiopulmonary function, autonomic regulation, and systemic stress. Sustained deviations from optimal ranges may indicate accelerated physiological wear, while stable, favourable patterns suggest healthier biological aging trajectories over time.`,
  respiratoryRatePattern: `Displays the average hourly variation in breathing rate across a 24-hour period, illustrating how respiration fluctuates throughout the day and night. Respiratory rate reflects metabolic demand, physical activity levels, and autonomic nervous system regulation.

By examining these hourly patterns, this view helps identify typical daytime elevations linked to movement and exertion, as well as nocturnal reductions associated with rest and recovery. Consistent deviations from expected patterns may indicate increased physiological stress, fatigue, or changes in overall health status.`,
  skinTemperaturePattern: `Displays the average hourly changes in skin temperature over a 24-hour period, reflecting circadian rhythm dynamics and peripheral thermoregulation.

Daily fluctuations can indicate shifts in metabolic activity, sleep-wake cycles, and physiological stress. Monitoring these patterns helps identify deviations from typical temperature rhythms that may signal changes in recovery status or overall health.`,
};

export const BiometricsAndVitals: React.FC = () => {
  const theme = useTheme();
  const { effectiveClientId, isCentralAdmin, isAnonymised } = useClientScope();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { filters: filterState, setFilters: setFilterState } =
    usePersistedFilters();
  const activeFiltersCount = getAppliedFilterCount(filterState);
  const { dateRange, setDateRange } = usePersistedDateRange();
  const [biometricTrendView, setBiometricTrendView] =
    useState("avg_heart_rate");

  const filterParams = useMemo(
    () => buildFilterParams(filterState, dateRange, effectiveClientId),
    [filterState, dateRange, effectiveClientId]
  );

  const granularity = useMemo(
    () => calculateGranularity(dateRange),
    [dateRange]
  );

  const overallRangeQuery = useMetricsOverallRange(effectiveClientId);

  const overallMinMax = useMemo(() => {
    const metric_min = overallRangeQuery.data?.data?.metric_min ?? {};
    const metric_max = overallRangeQuery.data?.data?.metric_max ?? {};
    return {
      avg_heart_rate: {
        min: typeof metric_min.avg_heart_rate === "number" ? metric_min.avg_heart_rate : undefined,
        max: typeof metric_max.avg_heart_rate === "number" ? metric_max.avg_heart_rate : undefined,
      },
      max_heart_rate: {
        min: typeof metric_min.max_heart_rate === "number" ? metric_min.max_heart_rate : undefined,
        max: typeof metric_max.max_heart_rate === "number" ? metric_max.max_heart_rate : undefined,
      },
      hrv: {
        min: typeof metric_min.hrv === "number" ? metric_min.hrv : undefined,
        max: typeof metric_max.hrv === "number" ? metric_max.hrv : undefined,
      },
      resting_heart_rate: {
        min: typeof metric_min.resting_heart_rate === "number" ? metric_min.resting_heart_rate : undefined,
        max: typeof metric_max.resting_heart_rate === "number" ? metric_max.resting_heart_rate : undefined,
      },
      respiratory_rate: {
        min: typeof metric_min.respiratory_rate === "number" ? metric_min.respiratory_rate : undefined,
        max: typeof metric_max.respiratory_rate === "number" ? metric_max.respiratory_rate : undefined,
      },
      spo2: {
        min: typeof metric_min.spo2 === "number" ? metric_min.spo2 : undefined,
        max: typeof metric_max.spo2 === "number" ? metric_max.spo2 : undefined,
      },
    };
  }, [overallRangeQuery.data]);

  // Cohort count for privacy guard
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ["active_users"] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  // Stats
  const statsQuery = useBiometricsStats({
    ...filterParams,
    metrics: [
      "respiratory_rate",
      "spo2",
      "pace_of_aging",
      "temperature",
      "resting_heart_rate",
      "hrv",
    ],
  });

  // Trends
  const trendsQuery = useBiometricsTrends({
    ...filterParams,
    metrics: [
      "avg_heart_rate",
      "max_heart_rate",
      "respiratory_rate",
      "spo2",
      "hrv",
      "resting_heart_rate",
    ],
    granularity,
  });

  // Patterns
  const respiratoryPatternsQuery = useBiometricsPatterns({
    ...filterParams,
    metric: "respiratory_rate_patterns",
    granularity: "hourly",
  });

  const tempPatternsQuery = useBiometricsPatterns({
    ...filterParams,
    metric: "skin_temperature_patterns",
    granularity: "hourly",
  });

  const handleApplyFilters = (filters: FilterState) => {
    setFilterState(filters);
    setIsFilterOpen(false);
  };

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
  };

  const trendsData = useMemo(() => {
    const metrics = [
      "avg_heart_rate",
      "max_heart_rate",
      "respiratory_rate",
      "spo2",
      "hrv",
      "resting_heart_rate",
    ];
    const transformed = transformTrendsData(trendsQuery.data, metrics);
    const lastWithData = [...transformed]
      .reverse()
      .findIndex((point) =>
        metrics.some((metric) => point[metric] !== null)
      );
    if (lastWithData === -1) return [];
    const lastIndex = transformed.length - 1 - lastWithData;
    return transformed.slice(0, lastIndex + 1);
  }, [trendsQuery.data]);

  const respiratoryRatePatternData = useMemo(() => {
    if (!respiratoryPatternsQuery.data?.data?.patterns) return [];
    return respiratoryPatternsQuery.data.data.patterns
      .filter((p) => p.time)
      .map((p) => ({
        time: p.time!,
        value: p.value,
      }));
  }, [respiratoryPatternsQuery.data]);

  const skinTemperaturePatternData = useMemo(() => {
    if (!tempPatternsQuery.data?.data?.patterns) return [];
    const patterns = tempPatternsQuery.data.data.patterns
      .filter((p) => p.time)
      .map((p) => ({
        time: p.time!,
        value: p.value,
      }));
    const values = patterns
      .map((item) => item.value)
      .filter((value): value is number => typeof value === "number");
    if (!values.length) return patterns;
    const average =
      Math.round(
        (values.reduce((sum, value) => sum + value, 0) / values.length) * 10
      ) / 10;
    return patterns.map((item) => ({
      ...item,
      value: item.value ?? average,
    }));
  }, [tempPatternsQuery.data]);

  const skinTemperatureAxis = useMemo(() => {
    const values = skinTemperaturePatternData
      .map((item) => item.value)
      .filter((value): value is number => typeof value === "number");
    if (!values.length) {
      return { min: 30, max: 38 };
    }
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = Math.max(0.5, (max - min) * 0.1);
    return {
      min: Math.floor((min - padding) * 10) / 10,
      max: Math.ceil((max + padding) * 10) / 10,
    };
  }, [skinTemperaturePatternData]);

  const statCards = useMemo(() => {
    if (!statsQuery.data?.data) return [];
    const stats = statsQuery.data.data;
    return [
      {
        metric: stats.resting_heart_rate,
        icon: FavoriteIcon,
      },
      {
        metric: stats.hrv,
        icon: MonitorHeartIcon,
      },
      {
        metric: stats.respiratory_rate,
        icon: AirIcon,
      },
      {
        metric: stats.spo2,
        icon: WaterDropIcon,
      },
    ].filter((card) => card.metric);
  }, [statsQuery.data]);

  const getTrendLabel = () => {
    switch (biometricTrendView) {
      case "avg_heart_rate":
        return "Avg HR";
      case "max_heart_rate":
        return "Max HR";
      case "hrv":
        return "Heart Rate Variability";
      case "resting_heart_rate":
        return "Resting HR";
      case "respiratory_rate":
        return "Respiratory Rate";
      case "spo2":
        return "Blood Oxygen (SpO2)";
      default:
        return "Metric";
    }
  };

  const getTrendColor = () => {
    switch (biometricTrendView) {
      case "avg_heart_rate":
        return theme.palette.info.main;
      case "max_heart_rate":
        return theme.palette.error.main;
      case "hrv":
        return theme.palette.success.main;
      case "resting_heart_rate":
        return theme.palette.warning.main;
      case "respiratory_rate":
        return theme.palette.warning.main;
      case "spo2":
        return theme.palette.success.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getYAxisMax = () => {
    switch (biometricTrendView) {
      case "avg_heart_rate":
      case "resting_heart_rate":
        return undefined; // Auto scale
      case "max_heart_rate":
        return undefined; // Auto scale
      case "hrv":
        return undefined; // Auto scale
      case "respiratory_rate":
        return 25;
      case "spo2":
        return 100;
      default:
        return undefined;
    }
  };

  const isLoading =
    statsQuery.isLoading ||
    trendsQuery.isLoading ||
    respiratoryPatternsQuery.isLoading ||
    tempPatternsQuery.isLoading;

  const hasError =
    statsQuery.isError ||
    trendsQuery.isError ||
    respiratoryPatternsQuery.isError ||
    tempPatternsQuery.isError;

  const getChipColor = (ragColor: string | undefined): any => {
    switch (ragColor) {
      case "green":
        return "success";
      case "amber":
        return "warning";
      case "red":
        return "error";
      default:
        return "default";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <DurationSelector
              value={dateRange}
              onChange={handleDateRangeChange}
              buttonVariant="outlined"
            />
            <Button
              variant="text"
              size="medium"
              startIcon={<FilterListIcon />}
              color={activeFiltersCount > 0 ? "primary" : "inherit"}
              onClick={() => setIsFilterOpen(true)}
            >
              {activeFiltersCount > 0 ? `FILTER (${activeFiltersCount})` : "FILTER"}
            </Button>
          </Box>
        </Box>

        <FilterDrawer
          open={isFilterOpen}
          onClose={() => setIsFilterOpen(false)}
          onApply={handleApplyFilters}
          initialFilters={filterState}
        />

        {hasError && (
          <Alert severity="error">
            Failed to load biometrics data. Please try again later.
          </Alert>
        )}

        <Grid container spacing={3}>
          {isLoading && statCards.length === 0 ? (
            <>
              {[1, 2, 3, 4].map((index) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                  <DashboardCardSkeleton />
                </Grid>
              ))}
            </>
          ) : (
            statCards.map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                <StatCard metric={stat.metric} icon={stat.icon} />
              </Grid>
            ))
          )}
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Biometric Trends"
              subtitle="Daily variations across core vital metrics."
              details={BIOMETRICS_AND_VITALS_DETAILS.biometricTrends}
              titleComponent={
                <ToggleButtonGroup
                  value={biometricTrendView}
                  exclusive
                  onChange={(_, newValue) => {
                    if (newValue !== null) setBiometricTrendView(newValue);
                  }}
                  size="small"
                >
                  <ToggleButton value="avg_heart_rate">Heart Rate</ToggleButton>
                  <ToggleButton value="hrv">
                    Heart Rate Variability
                  </ToggleButton>
                  <ToggleButton value="resting_heart_rate">
                    Resting HR
                  </ToggleButton>
                  <ToggleButton value="respiratory_rate">
                    Respiratory Rate
                  </ToggleButton>
                  <ToggleButton value="spo2">Blood Oxygen (SpO2)</ToggleButton>
                </ToggleButtonGroup>
              }
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : trendsData.length > 0 ? (
                  <LineChart
                    data={trendsData}
                    xAxisKey="label"
                    series={[
                      {
                        dataKey: biometricTrendView,
                        label: getTrendLabel(),
                        color: getTrendColor(),
                        area: true,
                      },
                      ...(biometricTrendView === "avg_heart_rate"
                        ? [
                            {
                              dataKey: "max_heart_rate",
                              label: "Max HR",
                              color: theme.palette.error.main,
                              dashed: true,
                            },
                            {
                              dataKey: "resting_heart_rate",
                              label: "Resting HR",
                              color: theme.palette.success.main,
                              dashed: true,
                            },
                          ]
                        : []),
                    ]}
                    height={300}
                    yAxisMin={0}
                    yAxisMax={getYAxisMax()}
                    showMinMaxLines
                    referenceMin={overallMinMax[biometricTrendView as keyof typeof overallMinMax]?.min}
                    referenceMax={overallMinMax[biometricTrendView as keyof typeof overallMinMax]?.max}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No trend data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <DashboardCard
              title="Average Pace of Aging"
              subtitle=""
              details={BIOMETRICS_AND_VITALS_DETAILS.averagePaceOfAging}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
              {isLoading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : statsQuery.data?.data?.pace_of_aging ? (
                <>
                  <Box sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "baseline",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 700,
                          fontSize: "2rem",
                        }}
                      >
                        {statsQuery.data.data.pace_of_aging.value}x
                      </Typography>
                      {(() => {
                        const paceChange =
                          statsQuery.data.data.pace_of_aging.change?.trim() || "";
                        const isNegative = paceChange.startsWith("-");
                        const isPositive = paceChange.startsWith("+");
                        const paceTrendColor = isNegative
                          ? theme.palette.error.main
                          : isPositive
                            ? theme.palette.success.main
                            : theme.palette.text.secondary;
                        const PaceTrendIcon = isNegative
                          ? TrendingDownIcon
                          : isPositive
                            ? TrendingUpIcon
                            : TrendingFlatIcon;

                        return (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.25,
                              color: paceTrendColor,
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.875rem",
                              }}
                            >
                              {paceChange}
                            </Typography>
                            <PaceTrendIcon
                              sx={{
                                fontSize: "0.875rem",
                                color: paceTrendColor,
                              }}
                            />
                          </Box>
                        );
                      })()}
                    </Box>
                  </Box>
                  <Stack spacing={2}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <AirIcon
                        fontSize="medium"
                        sx={{ color: theme.palette.primary.main }}
                      />
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        Respiration
                      </Typography>
                      <Chip
                        label={
                          statsQuery.data.data.respiratory_rate?.rag_label ||
                          "N/A"
                        }
                        size="small"
                        color={getChipColor(
                          statsQuery.data.data.respiratory_rate?.rag_color
                        )}
                      />
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <WaterDropIcon
                        fontSize="medium"
                        sx={{ color: theme.palette.primary.main }}
                      />
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        SpO2
                      </Typography>
                      <Chip
                        label={statsQuery.data.data.spo2?.rag_label || "N/A"}
                        size="small"
                        color={getChipColor(
                          statsQuery.data.data.spo2?.rag_color
                        )}
                      />
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <FavoriteIcon
                        fontSize="medium"
                        sx={{ color: theme.palette.primary.main }}
                      />
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        Resting HR
                      </Typography>
                      <Chip
                        label={
                          statsQuery.data.data.resting_heart_rate?.rag_label ||
                          "N/A"
                        }
                        size="small"
                        color={getChipColor(
                          statsQuery.data.data.resting_heart_rate?.rag_color
                        )}
                      />
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <MonitorHeartIcon
                        fontSize="medium"
                        sx={{ color: theme.palette.primary.main }}
                      />
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        HRV
                      </Typography>
                      <Chip
                        label={statsQuery.data.data.hrv?.rag_label || "N/A"}
                        size="small"
                        color={getChipColor(
                          statsQuery.data.data.hrv?.rag_color
                        )}
                      />
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <ThermostatIcon
                        fontSize="medium"
                        sx={{ color: theme.palette.primary.main }}
                      />
                      <Typography variant="body1" sx={{ flex: 1 }}>
                        Temperature
                      </Typography>
                      <Chip
                        label={
                          statsQuery.data.data.temperature?.rag_label || "N/A"
                        }
                        size="small"
                        color={getChipColor(
                          statsQuery.data.data.temperature?.rag_color
                        )}
                      />
                    </Box>
                  </Stack>
                </>
              ) : (
                <Alert severity="info">No Pace of Aging data</Alert>
              )}
              </CohortGuard>
            </DashboardCard>
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            <DashboardCard
              title="Respiratory Rate Pattern (24-Hour Average)"
              subtitle="Hourly variation in breathing rate throughout the day."
              details={BIOMETRICS_AND_VITALS_DETAILS.respiratoryRatePattern}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 230,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : respiratoryRatePatternData.length > 0 ? (
                  <LineChart
                    data={respiratoryRatePatternData}
                    xAxisKey="time"
                    series={[
                      {
                        dataKey: "value",
                        label: "Respiratory Rate",
                        color: theme.palette.warning.main,
                        area: true,
                      },
                    ]}
                    height={230}
                    yAxisMin={0}
                    yAxisMax={25}
                    showMinMaxLines
                    referenceMin={overallMinMax.respiratory_rate.min}
                    referenceMax={overallMinMax.respiratory_rate.max}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 230,
                    }}
                  >
                    <Alert severity="info">No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12 }}>
            <DashboardCard
              title="Skin Temperature Pattern (24-Hour Average)"
              subtitle="Tracks changes in skin temperature across a full day."
              details={BIOMETRICS_AND_VITALS_DETAILS.skinTemperaturePattern}
            >
              <CohortGuard
                userCount={activeUserCount}
                bypass={isCentralAdmin || !isAnonymised}
                loading={cohortQuery.isLoading}
              >
                {isLoading ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : skinTemperaturePatternData.length > 0 ? (
                  <LineChart
                    data={skinTemperaturePatternData}
                    xAxisKey="time"
                    series={[
                      {
                        dataKey: "value",
                        label: "Skin Temperature",
                        color: theme.palette.warning.main,
                      },
                    ]}
                    height={300}
                    yAxisMin={skinTemperatureAxis.min}
                    yAxisMax={skinTemperatureAxis.max}
                    showMinMaxLines
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: 300,
                    }}
                  >
                    <Alert severity="info">No pattern data available</Alert>
                  </Box>
                )}
              </CohortGuard>
            </DashboardCard>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
